// Faithful EAKC chat-product mockups — the real UI the bugs showed up in.
// 2D, frame-driven (reveal + typing), built on the Cold Compute kit. No emoji
// (headless Chrome renders color emoji unreliably) — geometric glyphs only.
import { interpolate, useCurrentFrame } from "remotion";
import { COLORS, FONTS } from "../../coldcompute";

type Status = "complete" | "skipped" | "in_progress" | "incorrect";

const pill: React.CSSProperties = {
  fontFamily: FONTS.mono,
  fontSize: 15,
  borderRadius: 999,
  padding: "4px 12px",
  border: `1px solid ${COLORS.line}`,
  whiteSpace: "nowrap",
};

/** Fade + rise in once `frame >= at`. */
export const Reveal: React.FC<{ at?: number; dur?: number; rise?: number; children: React.ReactNode }> = ({
  at = 0,
  dur = 12,
  rise = 16,
  children,
}) => {
  const frame = useCurrentFrame();
  const p = interpolate(frame, [at, at + dur], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return <div style={{ opacity: p, transform: `translateY(${(1 - p) * rise}px)` }}>{children}</div>;
};

/** Blinking text caret. */
export const Caret: React.FC<{ color?: string }> = ({ color = COLORS.cyan }) => {
  const frame = useCurrentFrame();
  const on = Math.floor(frame / 14) % 2 === 0;
  return (
    <span
      style={{
        display: "inline-block",
        width: 3,
        height: "0.95em",
        background: on ? color : "transparent",
        marginLeft: 4,
        transform: "translateY(4px)",
      }}
    />
  );
};

/** The app window frame (rounded, bordered, soft shadow). */
export const Win: React.FC<{ children: React.ReactNode; w?: number; style?: React.CSSProperties }> = ({
  children,
  w = 1480,
  style,
}) => (
  <div
    style={{
      width: w,
      background: COLORS.bg,
      border: `1px solid ${COLORS.line}`,
      borderRadius: 18,
      boxShadow: "0 50px 130px -45px #000, 0 0 0 1px #ffffff08 inset",
      overflow: "hidden",
      ...style,
    }}
  >
    {children}
  </div>
);

/** Chat header: question + build pill + agent chip. */
export const ChatHeader: React.FC<{ question: string; build?: string; agent?: string }> = ({
  question,
  build = "build 0531a",
  agent = "General",
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 14,
      padding: "18px 24px",
      borderBottom: `1px solid ${COLORS.line}`,
      background: COLORS.panel,
    }}
  >
    <div style={{ width: 18, height: 18, borderRadius: 5, background: COLORS.cyan, boxShadow: `0 0 16px ${COLORS.cyan}` }} />
    <div
      style={{
        flex: 1,
        color: COLORS.text,
        fontFamily: FONTS.display,
        fontWeight: 700,
        fontSize: 25,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {question}
    </div>
    <span style={{ ...pill, color: COLORS.green, borderColor: `${COLORS.green}66` }}>{build}</span>
    <span style={{ ...pill, color: COLORS.text, borderColor: COLORS.line, background: COLORS.panel2 }}>● {agent}</span>
  </div>
);

const STATUS_COLOR: Record<Status, string> = {
  complete: COLORS.green,
  skipped: COLORS.dim,
  in_progress: COLORS.cyan,
  incorrect: COLORS.red,
};

export const StatusPill: React.FC<{ status: Status; label?: string }> = ({ status, label }) => {
  const c = STATUS_COLOR[status];
  return (
    <span
      style={{
        fontFamily: FONTS.mono,
        fontSize: 14,
        color: c,
        border: `1px solid ${c}66`,
        borderRadius: 999,
        padding: "2px 11px",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        whiteSpace: "nowrap",
      }}
    >
      {label ?? status.replace("_", " ")}
    </span>
  );
};

/** One reasoning-trace row (the product's live pipeline log). */
export const Step: React.FC<{
  name: string;
  status: Status;
  time?: string;
  accent?: string;
  detail?: React.ReactNode;
  at?: number;
}> = ({ name, status, time, accent = COLORS.violet, detail, at = 0 }) => (
  <Reveal at={at}>
    <div
      style={{
        padding: "12px 16px",
        borderLeft: `3px solid ${accent}`,
        background: COLORS.panel,
        borderRadius: 8,
        marginBottom: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.text }}>{name}</span>
        <StatusPill status={status} />
        {time && <span style={{ fontFamily: FONTS.mono, fontSize: 14, color: COLORS.dim }}>{time}</span>}
      </div>
      {detail && (
        <div style={{ marginTop: 8, fontFamily: FONTS.mono, fontSize: 15, color: COLORS.dim, lineHeight: 1.6 }}>{detail}</div>
      )}
    </div>
  </Reveal>
);

/** An assistant answer block, optionally toned good/bad. */
export const Answer: React.FC<{ children: React.ReactNode; tone?: "neutral" | "bad" | "good"; title?: string }> = ({
  children,
  tone = "neutral",
  title,
}) => {
  const c = tone === "bad" ? COLORS.red : tone === "good" ? COLORS.green : COLORS.line;
  return (
    <div
      style={{
        background: COLORS.panel,
        border: `1px solid ${c}${tone === "neutral" ? "" : "66"}`,
        borderRadius: 12,
        padding: "20px 24px",
        fontFamily: FONTS.body,
        fontSize: 21,
        color: COLORS.text,
        lineHeight: 1.55,
      }}
    >
      {title && (
        <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 22, color: COLORS.text, marginBottom: 10 }}>
          {title}
        </div>
      )}
      {children}
    </div>
  );
};

/** Verdict / status banner. */
export const Banner: React.FC<{ tone: "good" | "bad" | "warn"; children: React.ReactNode }> = ({ tone, children }) => {
  const c = tone === "good" ? COLORS.green : tone === "bad" ? COLORS.red : COLORS.amber;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        background: `${c}16`,
        border: `1px solid ${c}66`,
        borderRadius: 10,
        padding: "13px 18px",
        fontFamily: FONTS.mono,
        fontSize: 18,
        color: COLORS.text,
      }}
    >
      <span style={{ color: c, fontWeight: 700 }}>{tone === "good" ? "✓" : tone === "bad" ? "✗" : "▍"}</span>
      {children}
    </div>
  );
};

/** The "Quality Assessment" card from the real truncation screenshot. */
export const QualityCard: React.FC<{ rows: { label: React.ReactNode; ok: boolean }[]; note?: React.ReactNode }> = ({
  rows,
  note,
}) => (
  <div
    style={{
      background: COLORS.panel,
      border: `1px solid ${COLORS.amber}44`,
      borderLeft: `3px solid ${COLORS.amber}`,
      borderRadius: 12,
      padding: "16px 20px",
    }}
  >
    <div
      style={{
        fontFamily: FONTS.mono,
        fontSize: 16,
        color: COLORS.amber,
        marginBottom: 12,
        textTransform: "uppercase",
        letterSpacing: "0.12em",
      }}
    >
      ▍ Quality Assessment
    </div>
    {rows.map((r, i) => (
      <div key={i} style={{ display: "flex", gap: 10, fontFamily: FONTS.mono, fontSize: 17, color: COLORS.text, marginBottom: 7 }}>
        <span style={{ color: r.ok ? COLORS.green : COLORS.red }}>{r.ok ? "✓" : "✗"}</span>
        {r.label}
      </div>
    ))}
    {note && <div style={{ marginTop: 10, fontFamily: FONTS.mono, fontSize: 15, color: COLORS.dim }}>{note}</div>}
  </div>
);

/** A follow-up suggestion chip. */
export const Chip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      border: `1px solid ${COLORS.line}`,
      borderRadius: 999,
      padding: "12px 22px",
      color: COLORS.text,
      fontFamily: FONTS.body,
      fontSize: 18,
      background: COLORS.panel,
    }}
  >
    {children}
  </div>
);

/** Composer bar at the bottom of the chat window. */
export const BottomBar: React.FC<{ input?: string; skills?: number }> = ({ input = "Ask anything…", skills = 30 }) => (
  <div style={{ borderTop: `1px solid ${COLORS.line}`, background: COLORS.panel, padding: "14px 20px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{ ...pill, color: COLORS.cyan, borderColor: `${COLORS.cyan}55` }}>General · PRIMARY ✕</span>
      <span style={{ ...pill, color: COLORS.dim, borderColor: COLORS.line }}>+ Add agent</span>
      <div style={{ flex: 1 }} />
      <span style={{ ...pill, color: COLORS.violet, borderColor: `${COLORS.violet}55` }}>✦ {skills} skills</span>
      <span style={{ fontFamily: FONTS.mono, fontSize: 15, color: COLORS.green }}>● Nominal</span>
    </div>
    <div
      style={{
        marginTop: 12,
        display: "flex",
        alignItems: "center",
        gap: 12,
        border: `1px solid ${COLORS.line}`,
        borderRadius: 12,
        padding: "12px 16px",
        background: COLORS.bg,
      }}
    >
      <span style={{ ...pill, color: COLORS.text, borderColor: COLORS.line }}>Chat ⌄</span>
      <span style={{ flex: 1, fontFamily: FONTS.body, fontSize: 18, color: COLORS.dim }}>{input}</span>
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 9,
          background: COLORS.cyan,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: COLORS.bg,
          fontSize: 18,
          fontWeight: 700,
        }}
      >
        ↑
      </div>
    </div>
  </div>
);

/** Code diff line (for showing a fix). */
export const Diff: React.FC<{ sign: "-" | "+" | " "; children: React.ReactNode }> = ({ sign, children }) => {
  const c = sign === "-" ? COLORS.red : sign === "+" ? COLORS.green : COLORS.dim;
  const bg = sign === "-" ? `${COLORS.red}12` : sign === "+" ? `${COLORS.green}12` : "transparent";
  return (
    <div style={{ display: "flex", gap: 14, fontFamily: FONTS.mono, fontSize: 18, background: bg, padding: "4px 12px", borderRadius: 4 }}>
      <span style={{ color: c, width: 14 }}>{sign}</span>
      <span style={{ color: sign === " " ? COLORS.dim : COLORS.text }}>{children}</span>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
